import { useImperativeHandle, useRef } from "react";

import {
  FieldDefinitionBooleanType,
  FieldDefinitionFieldType,
  FieldImperativeHandle,
} from "@/components/@types";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";

function FieldBooleanValue<T extends FieldDefinitionBooleanType<T>>({
  value,
  events,
  ref,
}: FieldDefinitionFieldType<FieldDefinitionBooleanType<T>> & {
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const isChecked = value || false;
  const isDisabled = !events?.onChangeField || !events?.onSubmitField;
  const checkboxRef = useRef<HTMLButtonElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      setFocus: () => {
        checkboxRef.current?.focus();
      },
      unsetFocus: () => {
        checkboxRef.current?.blur();
      },
    }),
    [],
  );

  const handleClick = () => {
    if (!isDisabled) {
      events?.onChangeField?.(!isChecked);
      events?.onSubmitField?.();
    }
  };

  return (
    <Checkbox
      checked={isChecked}
      ref={checkboxRef}
      data-testid={`field-boolean-${isChecked ? "checked" : "unchecked"}`}
      tabIndex={events ? 0 : -1}
      disabled={isDisabled}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && !isDisabled) {
          e.preventDefault();
          handleClick();
        }
      }}
      className="pt-1"
      onCheckedChange={(value) => {
        const checked = value === true;
        if (!isDisabled) {
          events?.onChangeField?.(checked);
          events?.onSubmitField?.();
        }
      }}
    />
  );
}

function FieldBooleanSkeleton() {
  return (
    <Skeleton
      variant="rectangular"
      style={{
        minWidth: 16,
        minHeight: 16,
        maxWidth: 16,
        maxHeight: 16,
        borderRadius: "0.25rem",
        // marginLeft: theme.spacing(0.5),
      }}
    />
  );
}

export function FieldBoolean<T extends FieldDefinitionBooleanType<T>>({
  isLoading,
  ref,
  ...props
}: FieldDefinitionFieldType<FieldDefinitionBooleanType<T>> & {
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  if (isLoading) {
    return <FieldBooleanSkeleton />;
  }

  return <FieldBooleanValue {...props} ref={ref} />;
}
